/**
 * Storage de cupons de desconto (admin).
 * Persistido em localStorage.
 * Suporta cupom da plataforma e cupom por estabelecimento.
 */

export type TipoDescontoCupom = 'percentual' | 'valor_fixo';
export type EscopoCupom = 'plataforma' | 'estabelecimento' | 'experiencia';

export interface Cupom {
  id: string;
  /** Código único (ex: NAVI10, VERAO2025) */
  codigo: string;
  /** Descrição opcional */
  descricao?: string;
  /** Tipo do desconto */
  tipoDesconto: TipoDescontoCupom;
  /** Valor: % (0-100) ou R$ fixo */
  valor: number;
  /** Escopo: plataforma (todos), estabelecimento, ou experiência específica */
  escopo: EscopoCupom;
  /** ID do estabelecimento (quando escopo = estabelecimento) */
  estabelecimentoId?: string;
  /** IDs das experiências (quando escopo = experiencia) */
  experienciaIds?: string[];
  /** Data de início de validade (YYYY-MM-DD) */
  dataInicio: string;
  /** Data de fim de validade (YYYY-MM-DD) */
  dataFim: string;
  /** Usos máximos (0 = ilimitado) */
  usosMaximos: number;
  /** Usos já realizados */
  usosAtuais: number;
  /** Ativo/inativo */
  ativo: boolean;
  /** Rascunho no admin até finalizar cadastro */
  rascunho?: boolean;
  /** Valor mínimo do pedido para aplicar (R$) */
  valorMinimoPedido?: number;
  /** Imagem/QR Code em base64 (opcional) */
  qrCodeBase64?: string;
  createdAt: string;
  updatedAt: string;
}

const KEY_CUPONS = 'naviex-admin-cupons';

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getCupons(): Cupom[] {
  return loadJson<Cupom[]>(KEY_CUPONS, []);
}

export function getCupomById(id: string): Cupom | null {
  return getCupons().find((c) => c.id === id) ?? null;
}

export function getCupomByCodigo(codigo: string): Cupom | null {
  const cod = codigo.trim().toUpperCase();
  return getCupons().find((c) => c.codigo.toUpperCase() === cod) ?? null;
}

export function getCuponsByEstabelecimento(estabelecimentoId: string): Cupom[] {
  return getCupons().filter((c) => c.estabelecimentoId === estabelecimentoId);
}

const CUPOM_CODIGO_LENGTH = 8;

/** Código único de 8 caracteres para rascunhos automáticos. */
export function generateDraftCupomCodigo(): string {
  const list = getCupons();
  for (let i = 0; i < 80; i++) {
    const raw = `${Date.now()}${Math.random().toString(36).slice(2)}`.replace(/[^0-9A-Z]/gi, '').toUpperCase();
    const code = raw.slice(-CUPOM_CODIGO_LENGTH).padStart(CUPOM_CODIGO_LENGTH, 'R');
    if (!list.some((c) => c.codigo.toUpperCase() === code)) return code;
  }
  return `R${Math.random().toString(36).slice(2, 9).toUpperCase().padEnd(7, '0')}`.slice(0, CUPOM_CODIGO_LENGTH);
}

export function addCupom(data: Omit<Cupom, 'id' | 'usosAtuais' | 'createdAt' | 'updatedAt'>): Cupom {
  const now = new Date().toISOString();
  const list = getCupons();
  let codigoUpper = data.codigo.trim().toUpperCase();
  if (data.rascunho) {
    if (codigoUpper.length !== CUPOM_CODIGO_LENGTH) {
      codigoUpper = generateDraftCupomCodigo();
    }
  } else if (codigoUpper.length !== CUPOM_CODIGO_LENGTH) {
    throw new Error(`O código do cupom deve ter exatamente ${CUPOM_CODIGO_LENGTH} caracteres.`);
  }
  if (list.some((c) => c.codigo.toUpperCase() === codigoUpper)) {
    throw new Error('Já existe um cupom com este código.');
  }
  const next: Cupom = {
    ...data,
    codigo: codigoUpper,
    id: `cupom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    usosAtuais: 0,
    createdAt: now,
    updatedAt: now,
  };
  saveJson(KEY_CUPONS, [...list, next]);
  return next;
}

export function saveCupom(cupom: Cupom): void {
  const list = getCupons();
  const idx = list.findIndex((c) => c.id === cupom.id);
  if (idx < 0) throw new Error('Cupom não encontrado.');
  let codigoUpper = cupom.codigo.trim().toUpperCase();
  if (cupom.rascunho) {
    if (codigoUpper.length !== CUPOM_CODIGO_LENGTH) {
      codigoUpper = generateDraftCupomCodigo();
    }
  } else if (codigoUpper.length !== CUPOM_CODIGO_LENGTH) {
    throw new Error(`O código do cupom deve ter exatamente ${CUPOM_CODIGO_LENGTH} caracteres.`);
  }
  const duplicado = list.find((c, i) => i !== idx && c.codigo.toUpperCase() === codigoUpper);
  if (duplicado) throw new Error('Já existe outro cupom com este código.');
  const updated = { ...cupom, codigo: codigoUpper, updatedAt: new Date().toISOString() };
  const next = [...list];
  next[idx] = updated;
  saveJson(KEY_CUPONS, next);
}

export function removeCupom(id: string): void {
  const list = getCupons().filter((c) => c.id !== id);
  saveJson(KEY_CUPONS, list);
}

/**
 * Valida um cupom para um pedido (usado no cliente via API ou mock).
 * Retorna o valor do desconto em R$ ou null se inválido.
 */
export function validarCupom(
  codigo: string,
  subtotal: number,
  estabelecimentoId?: string,
  experienciaIds?: string[]
): { valido: true; descontoValor: number; cupom: Cupom } | { valido: false; mensagem: string } {
  const cupom = getCupomByCodigo(codigo);
  if (!cupom) return { valido: false, mensagem: 'Cupom não encontrado.' };
  if (cupom.rascunho) return { valido: false, mensagem: 'Cupom ainda não está disponível.' };
  if (!cupom.ativo) return { valido: false, mensagem: 'Cupom inativo.' };
  const hoje = new Date().toISOString().slice(0, 10);
  if (cupom.dataInicio > hoje) return { valido: false, mensagem: 'Cupom ainda não está válido.' };
  if (cupom.dataFim < hoje) return { valido: false, mensagem: 'Cupom expirado.' };
  if (cupom.usosMaximos > 0 && cupom.usosAtuais >= cupom.usosMaximos) {
    return { valido: false, mensagem: 'Cupom esgotado.' };
  }
  if (cupom.valorMinimoPedido != null && subtotal < cupom.valorMinimoPedido) {
    return { valido: false, mensagem: `Pedido mínimo de R$ ${cupom.valorMinimoPedido.toLocaleString('pt-BR')}.` };
  }
  if (cupom.escopo === 'estabelecimento' && estabelecimentoId && cupom.estabelecimentoId !== estabelecimentoId) {
    return { valido: false, mensagem: 'Cupom não válido para este estabelecimento.' };
  }
  if (cupom.escopo === 'experiencia' && experienciaIds?.length) {
    const idsSet = new Set(cupom.experienciaIds ?? []);
    const temAlguma = experienciaIds.some((id) => idsSet.has(id));
    if (!temAlguma) return { valido: false, mensagem: 'Cupom não válido para as experiências selecionadas.' };
  }
  let descontoValor = 0;
  if (cupom.tipoDesconto === 'percentual') {
    descontoValor = Math.round(subtotal * (Math.min(100, Math.max(0, cupom.valor)) / 100));
  } else {
    descontoValor = Math.min(subtotal, Math.max(0, cupom.valor));
  }
  if (descontoValor <= 0) return { valido: false, mensagem: 'Desconto não aplicável a este pedido.' };
  return { valido: true, descontoValor, cupom };
}
